import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'
import type { Asset, MathseedsPrimeLessonEntry } from 'teacher-ui-client/contentful-client/entry-types'
import { LessonResourceType } from 'teacher-ui-client/contentful-client/entry-types'
import { parse } from 'teacher-ui-client/utils/uri'
import { MathseedsPrimeLessonPreview } from 'teacher-ui-client/interactive-preview/engines'
import type InteractivePreviewService from 'teacher-ui-client/services/interactive-preview'
import { LessonsPage } from 'teacher-ui-client/components/page/lessons'
import { LessonsSearch } from 'teacher-ui-client/components/lessons/search'
import { LessonPreviewTile } from 'teacher-ui-client/components/tiles/lesson/preview'
import { MathseedsPrimeThreadFilter } from 'teacher-ui-client/components/lessons/mathseeds-prime/thread-filter'
import { LessonFields } from 'teacher-ui-client/components/lesson/fields'
import { LessonField } from 'teacher-ui-client/components/lesson/field'
import { NoResultsMessage } from 'teacher-ui-client/components/no-results-message'
import { t } from 'ember-intl'
import { BasePageSelector } from 'teacher-ui-client/components/base/page-selector'
import type { MathseedsPrimeThread } from 'teacher-ui-client/progression-sequence/utils'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type MathseedsPrimeLessonsRoute from 'teacher-ui-client/subscription-type/lessons/mathseeds-prime/route'

export type MathseedsPrimeLessonUrlComponents = Pick<
  MathseedsPrimeLessonEntry['fields'],
  'curriculumId' | 'gradeId' | 'threadId' | 'unitId' | 'lessonId'
>

interface Args {
  model: ModelFor<MathseedsPrimeLessonsRoute>
}

interface LessonPreviewViewModel {
  curriculumId: 'au'
  gradeId: number
  threadId: MathseedsPrimeThread
  unitId: number
  unitName: string
  lessonId: number
  lessonName: string
  cover?: Asset
  resources: {
    title: string
    resourceType: LessonResourceType
    id: string
    fileUrl?: string
  }[]
  content?: string
  id: string
  title: string
  lessonNumber: string
  openPreview: () => void
  coverUrl?: string
  [key: string]: unknown
}

function coverViewModel(cover?: Asset) {
  if (cover && cover.fields.file?.url && cover.fields.file.details && 'image' in cover.fields.file.details) {
    const coverUrl = parse(cover.fields.file.url) // parse the protocol-relative url (missing http/https)
    coverUrl.searchParams.append('fm', 'jpg') // https://www.contentful.com/developers/docs/references/images-api
    coverUrl.searchParams.append('fl', 'progressive')
    coverUrl.searchParams.append('h', '140')
    return {
      coverUrl: coverUrl.href,
    }
  }
  return undefined
}

export class MathseedsPrimePreviewsPage extends Component<Args> {
  @service intl!: IntlService

  @service interactivePreview!: InteractivePreviewService

  private readonly resourceOrder: LessonResourceType[] = [
    LessonResourceType.STUDENT_PDF,
    LessonResourceType.TEACHER_PDF,
    LessonResourceType.ES_STUDENT_PDF,
  ]

  get lessonPreviewViewModels(): LessonPreviewViewModel[] {
    if (!this.args.model.loaderInstance?.isSuccessful) return []
    const lessons = this.args.model.loaderInstance.value?.lessons?.items ?? []
    return lessons.map((lesson) => ({
      ...lesson.fields,
      id: lesson.sys.id,
      title: this.titleForLesson(lesson.fields),
      lessonNumber: this.segmentedLessonNumber(lesson.fields),
      ...coverViewModel(lesson.fields.cover),
      openPreview: this.getOpenPreview(lesson.fields),
      resources:
        lesson.fields.resources
          ?.filter(<T,>(resource: T | undefined): resource is T => Boolean(resource))
          ?.map((resource) => ({
            id: resource.sys.id,
            title: resource.fields.title,
            resourceType: resource.fields.resourceType,
            fileUrl: resource.fields.file?.fields.file?.url,
          }))
          .sort((a, b) => this.resourceOrder.indexOf(a.resourceType) - this.resourceOrder.indexOf(b.resourceType)) ??
        [],
    }))
  }

  titleForLesson({
    lessonId,
    unitId,
    threadId,
  }: Pick<MathseedsPrimeLessonEntry['fields'], 'lessonId' | 'unitId' | 'threadId'>) {
    const threadName = this.intl.t(`mathseedsPrimeThreads.${threadId}.name`)
    return this.intl.t('components.tiles.mathseedsPrimeLessonPreview.title', { threadName, unitId, lessonId })
  }

  /**
   * @example
   * segmentedLessonNumber({ lessonId: 2, unitId: 23, threadId: 'number' }
   * > "NUM.23.2"
   */
  segmentedLessonNumber({ lessonId, unitId, threadId }: MathseedsPrimeLessonEntry['fields']) {
    const threadCode = this.intl.t(`mathseedsPrimeThreads.${threadId}.code`)
    return `${threadCode}.${unitId}.${lessonId}`
  }

  getOpenPreview({ lessonId, unitId, threadId }: MathseedsPrimeLessonEntry['fields']) {
    return () => {
      const preview = new MathseedsPrimeLessonPreview(
        this,
        this.interactivePreview.curriculum,
        threadId,
        unitId,
        lessonId,
      )
      this.interactivePreview.openPreview(preview, { title: this.titleForLesson({ lessonId, unitId, threadId }) })
    }
  }

  <template>
    <LessonsPage @title={{t "pages.product.lessons.mathseedsPrime.title"}} @loaderInstance={{@model.loaderInstance}}>
      <:header>
        <LessonsSearch
          class="w-80"
          @placeholder={{t "pages.product.lessons.mathseedsPrime.searchPlaceholder"}}
          @themeKey={{ThemeKeys.green}}
        />
        <MathseedsPrimeThreadFilter />
      </:header>
      <:results>
        {{#each this.lessonPreviewViewModels as |viewModel|}}
          <LessonPreviewTile
            data-test-lesson-preview-entry={{viewModel.id}}
            class="w-80"
            @entryId={{viewModel.id}}
            @title={{viewModel.lessonName}}
            @subtitle={{viewModel.title}}
            @coverUrl={{viewModel.coverUrl}}
            @coverClass="h-40"
            @lessonResources={{viewModel.resources}}
            @openPreview={{viewModel.openPreview}}
          >
            <LessonFields>
              <LessonField @key="lessonNumber" @fields={{viewModel}} />
              <LessonField @key="content" @fields={{viewModel}} />
            </LessonFields>
          </LessonPreviewTile>
        {{else}}
          <NoResultsMessage data-test-empty />
        {{/each}}
        {{#if @model.loaderInstance.value.pageCount}}
          <BasePageSelector
            class="basis-full"
            @pageCount={{@model.loaderInstance.value.pageCount}}
            @itemCount={{@model.loaderInstance.value.lessons.total}}
          />
        {{/if}}
      </:results>
    </LessonsPage>
  </template>
}

export default RouteTemplate(MathseedsPrimePreviewsPage)
